import { useState, useCallback, useMemo } from 'react'

interface Options<T> {
  itemKey?: keyof T | ((item: T) => React.Key)
  defaultSelected?: T[]
  maxCount?: number
}

export function useBatch<T>(
  items: T[],
  options: Options<T> & {
    defaultBatchMode?: boolean
    maxCount?: number
  } = {},
) {
  const [batchMode, setBatchMode] = useState(options.defaultBatchMode ?? false)
  const [selected, setSelected] = useState<T[]>(options.defaultSelected ?? [])
  const maxCount = options.maxCount ?? Infinity

  const itemKey = useCallback(
    (item: T) => {
      if (options?.itemKey) {
        return typeof options.itemKey === 'function' ? options.itemKey(item) : item[options.itemKey]
      }
      return item
    },
    [options.itemKey],
  )

  const isSelected = useCallback(
    (item: T) => {
      return selected.some((selectedItem) => itemKey(selectedItem) === itemKey(item))
    },
    [selected, itemKey],
  )

  const select = useCallback(
    (item: T) => {
      if (selected.length < maxCount) {
        setSelected([...selected, item])
      }
    },
    [selected, isSelected, itemKey, maxCount],
  )

  const unSelect = useCallback(
    (item: T) => {
      setSelected(selected.filter((selectedItem) => itemKey(selectedItem) !== itemKey(item)))
    },
    [selected, itemKey],
  )

  const toggle = useCallback(
    (item: T) => {
      if (isSelected(item)) {
        unSelect(item)
      } else if (selected.length < maxCount) {
        select(item)
      }
    },
    [isSelected, unSelect, select, selected.length, maxCount],
  )

  const selectAll = useCallback(() => {
    // 合并已选和当前页未选，去重
    const merged = [...selected, ...items.filter((item) => !isSelected(item))]
    // 限制最大数量
    if (merged.length > maxCount) {
      setSelected(merged.slice(0, maxCount))
    } else {
      setSelected(merged)
    }
  }, [items, maxCount, selected, isSelected])

  const unSelectAll = useCallback(() => {
    setSelected(
      selected.filter((item) => {
        const key = itemKey(item)
        return !items.some((item) => itemKey(item) === key)
      }),
    )
  }, [items, selected, isSelected])

  const noneSelected = useMemo(() => selected.length === 0, [selected.length])
  const allSelected = useMemo(() => {
    if (items.length === 0) return false
    // 针对分页场景修改，判断当前页是否全选
    return items.every((item) => isSelected(item))
  }, [items, isSelected])
  const partiallySelected = useMemo(() => !noneSelected && !allSelected, [noneSelected, allSelected])

  const setSelectedItems = useCallback(
    (items: T[]) => {
      if (items.length <= maxCount) {
        setSelected(items)
      } else {
        setSelected(items.slice(0, maxCount))
      }
    },
    [maxCount],
  )
  const toggleAll = useCallback(() => {
    if (allSelected) {
      unSelectAll()
    } else {
      selectAll()
    }
  }, [allSelected, selectAll, unSelectAll])
  const clearAll = useCallback(() => {
    setSelected([])
  }, [])
  return {
    batchMode,
    setBatchMode: (mode: boolean) => {
      if (!mode) {
        setSelected([])
      }
      setBatchMode(mode)
    },
    selected,
    isSelected,
    select,
    unSelect,
    toggle,
    selectAll,
    unSelectAll,
    toggleAll,
    noneSelected,
    allSelected,
    partiallySelected,
    setSelected: setSelectedItems,
    clearAll,
  }
}
